import { useState } from 'react'
import { useToast } from '@chakra-ui/react'
import { useRouterState } from '@tanstack/react-router'
import { useSelector } from 'react-redux'
import { selectIsLoggedIn } from '@/store/slices/authSlice'
import { useOnboardingStatus, canGatedMemberAccessPath } from '@/api/queries/useOnboardingQueries'
import { analytics } from '@/lib/analytics'
import { AnalyticsEvents } from '@/lib/analytics/events'
import { tabBarGateToastCopy } from '@/copy'

const PUBLIC_PAGES = ['/restaurants/', '/lists/', '/profile/']

/**
 * Whether the current route is a quiz-taking page (nav shell hidden).
 * Split out from `useNavGateBehaviors` (D45b review fix A) so consumers
 * that only need this one boolean — like `TabLayout`, which decides
 * whether to reserve the desktop rail's width — don't have to pull in the
 * full gate hook's Redux/toast/query dependencies, and so the rail's
 * render-or-not decision and the layout's gutter decision can never drift
 * from each other.
 */
export function useIsQuizTaking() {
  const { location } = useRouterState()
  // Hide nav shell when taking a quiz (but not on /quizzes overview)
  return location.pathname.startsWith('/quizzes/') && location.pathname !== '/quizzes/'
}

/**
 * Shared nav-shell behaviors (D45b review fix N1): onboarding gate check +
 * toast, the logged-out sign-up prompt on public pages, quiz-taking
 * detection, and analytics dispatch. `MainNavigationTabBar` (mobile) and
 * `DesktopNavRail` (desktop) mount as siblings for the SAME route trees
 * (`_authenticated.tsx`, `_publicWithTabs.tsx`), so both need identical
 * guard clauses and side effects — this hook is the single source of truth
 * so neither surface can drift from the other.
 */
export function useNavGateBehaviors() {
  const { location } = useRouterState()
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false)
  const toast = useToast()
  const { data: onboardingStatus } = useOnboardingStatus()

  const isQuizTaking = useIsQuizTaking()

  const isOnPublicPage = PUBLIC_PAGES.some(page => location.pathname.startsWith(page))

  /**
   * Call on every nav-item click (and the desktop rail's `⋯` menu trigger).
   * Dispatches analytics, then applies the sign-up gate and the onboarding
   * gate toast. Returns `false` when the caller should stop short (sign-up
   * modal shown instead of navigating) and `true` when it's safe to
   * proceed with navigation / opening the Menu overlay.
   */
  const handleNavItemClick = (to: string, label: string): boolean => {
    analytics.capture(AnalyticsEvents.BOTTOM_NAV_CLICKED, {
      tab_name: label.toLowerCase(),
      destination: to,
    })

    if (to === '/menu') {
      analytics.capture(AnalyticsEvents.BURGER_MENU_CLICKED)
    }

    if (!isLoggedIn && isOnPublicPage) {
      setIsSignUpModalOpen(true)
      return false
    }

    const requiredQuizKey = onboardingStatus?.nextRequiredQuizKey
    if (onboardingStatus?.needsQuiz && requiredQuizKey && !canGatedMemberAccessPath(to, requiredQuizKey)) {
      toast({
        title: tabBarGateToastCopy.title,
        description: tabBarGateToastCopy.description,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      // Let the navigation proceed — OnboardingGuard will redirect to quiz
    }

    return true
  }

  return {
    isQuizTaking,
    isSignUpModalOpen,
    setIsSignUpModalOpen,
    handleNavItemClick,
  }
}
